import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const MapboxLocationSearch = ({ 
  value = '', 
  onChange, 
  placeholder = "Search for a location...",
  style = {},
  onFocus,
  onBlur,
  required = false
}) => {
  const [searchValue, setSearchValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const timeoutRef = useRef(null);
  const searchBoxRef = useRef(null);

  // const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  const MAPBOX_TOKEN = 'sk.eyJ1IjoidnZsbGwyZmxyaSIsImEiOiJjbWthajBoM3AyNWJuM2VwdHRqYzh1djZzIn0.uuQQJvrKeBq8zv38EWFXYA';

  // Generate session token for Search Box API
  useEffect(() => {
    setSessionToken(crypto.randomUUID());
  }, []);

  // Update local state when prop value changes
  useEffect(() => {
    setSearchValue(value);
    if (value && !selectedLocation) {
      // If there's a value but no selected location, it might be from initial data
      setSelectedLocation({ name: value, shortName: value });
    }
  }, [value]);

  // Search using Mapbox Search Box API for better POI results
  const searchPlaces = async (query) => {
    if (!query.trim() || !MAPBOX_TOKEN || MAPBOX_TOKEN === 'your_mapbox_token_here' || !sessionToken) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `https://api.mapbox.com/search/searchbox/v1/suggest?` +
        `q=${encodeURIComponent(query)}&` +
        `access_token=${MAPBOX_TOKEN}&` +
        `session_token=${sessionToken}&` +
        `language=en&` +
        `limit=8&` +
        `types=poi,address,place`
      );

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      } else {
        console.error('Mapbox Search Box API error:', response.statusText);
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error searching places:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Retrieve full details for a selected suggestion
  const retrieveLocation = async (suggestion) => {
    if (!suggestion.mapbox_id || !sessionToken) {
      return null;
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/search/searchbox/v1/retrieve/${suggestion.mapbox_id}?` +
        `access_token=${MAPBOX_TOKEN}&` +
        `session_token=${sessionToken}`
      );

      if (response.ok) {
        const data = await response.json();
        return data.features?.[0] || null;
      } else {
        console.error('Mapbox retrieve error:', response.statusText);
        return null;
      }
    } catch (error) {
      console.error('Error retrieving location details:', error);
      return null;
    }
  };

  // Handle search input change with debouncing
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Clear selected location when user starts typing
    if (searchValue !== selectedLocation?.name) {
      setSelectedLocation(null);
    }

    timeoutRef.current = setTimeout(() => {
      if (searchValue && !selectedLocation) {
        searchPlaces(searchValue);
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchValue, selectedLocation]);

  // Handle input change
  const handleInputChange = (event) => {
    const newValue = event.target.value;
    setSearchValue(newValue);
    setShowSuggestions(true);
    
    // Call parent onChange with the raw text value
    onChange(newValue);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = async (suggestion) => {
    // Use suggestion data directly for immediate feedback
    const location = {
      id: suggestion.mapbox_id || Date.now(),
      name: suggestion.full_address || suggestion.name,
      shortName: suggestion.name,
      mapbox_id: suggestion.mapbox_id,
      placeType: suggestion.feature_type || 'poi'
    };

    setSelectedLocation(location);
    setSearchValue(location.name);
    setSuggestions([]);
    setShowSuggestions(false);
    
    // Call parent onChange with the location name
    onChange(location.name);

    // Optionally retrieve full details in background for future use
    if (suggestion.mapbox_id) {
      try {
        const fullDetails = await retrieveLocation(suggestion);
        if (fullDetails) {
          // Could store additional details if needed
          console.log('Full location details:', fullDetails);
        }
      } catch (error) {
        console.error('Error retrieving full details:', error);
      }
    }
  };

  // Handle clear button
  const handleClear = () => {
    setSearchValue('');
    setSelectedLocation(null);
    setSuggestions([]);
    setShowSuggestions(false);
    onChange('');
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get place icon based on Search Box API feature type
  const getPlaceIcon = (suggestion) => {
    const featureType = suggestion.feature_type;
    const poiCategory = suggestion.poi_category || [];
    
    // Check POI categories first for more specific icons
    if (poiCategory.includes('restaurant')) return '🍽️';
    if (poiCategory.includes('hotel')) return '🏨';
    if (poiCategory.includes('gas_station')) return '⛽';
    if (poiCategory.includes('shopping')) return '🛍️';
    if (poiCategory.includes('museum')) return '🏛️';
    if (poiCategory.includes('park')) return '🌳';
    if (poiCategory.includes('hospital')) return '🏥';
    if (poiCategory.includes('school')) return '🏫';
    if (poiCategory.includes('bank')) return '🏦';
    if (poiCategory.includes('airport')) return '✈️';
    if (poiCategory.includes('train_station')) return '🚂';
    if (poiCategory.includes('church')) return '⛪';
    if (poiCategory.includes('entertainment')) return '🎭';
    if (poiCategory.includes('tourist_attraction')) return '🎯';
    
    // Fallback to feature type
    switch (featureType) {
      case 'poi':
        return '🏛️';
      case 'place':
        return '🏙️';
      case 'address':
        return '📍';
      case 'street':
        return '🛣️';
      default:
        return '📍';
    }
  };

  return (
    <div ref={searchBoxRef} style={{ position: 'relative', width: '100%', ...style }}>
      <TextField
        fullWidth
        value={searchValue}
        onChange={handleInputChange}
        onFocus={(e) => {
          setShowSuggestions(true);
          onFocus && onFocus(e);
        }}
        onBlur={(e) => {
          // Delay hiding suggestions to allow for clicks
          setTimeout(() => setShowSuggestions(false), 150);
          onBlur && onBlur(e);
        }}
        placeholder={placeholder}
        required={required}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LocationIcon sx={{ color: '#FB923C' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {loading && <CircularProgress size={20} sx={{ color: '#FB923C' }} />}
              {searchValue && !loading && (
                <ClearIcon 
                  sx={{ 
                    color: '#666', 
                    cursor: 'pointer',
                    '&:hover': { color: '#FB923C' }
                  }}
                  onClick={handleClear}
                />
              )}
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.8)',
            border: '1px solid #FED7AA',
            '&:hover': {
              borderColor: '#FB923C',
            },
            '&.Mui-focused': {
              borderColor: '#FB923C',
              background: 'white',
              boxShadow: '0 0 0 4px rgba(251, 146, 60, 0.1)'
            },
            '& fieldset': {
              border: 'none'
            }
          },
          '& .MuiInputBase-input': {
            padding: '12px 16px',
            fontSize: '16px'
          }
        }}
      />

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: '300px',
            overflow: 'auto',
            marginTop: '4px',
            borderRadius: '16px',
            boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid #FED7AA'
          }}
        >
          <List sx={{ padding: 0 }}>
            {suggestions.map((suggestion, index) => (
              <ListItem
                key={suggestion.mapbox_id || index}
                component="div"
                onClick={() => handleSuggestionSelect(suggestion)}
                sx={{
                  cursor: 'pointer',
                  padding: '12px 16px',
                  borderBottom: index < suggestions.length - 1 ? 
                    '1px solid rgba(0, 0, 0, 0.05)' : 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: '40px' }}>
                  <Typography sx={{ fontSize: '20px' }}>
                    {getPlaceIcon(suggestion)}
                  </Typography>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ 
                      fontWeight: 600,
                      color: '#374151',
                      fontSize: '0.95rem'
                    }}>
                      {suggestion.name}
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{ 
                      color: '#6B7280',
                      fontSize: '0.85rem',
                      marginTop: '2px'
                    }}>
                      {suggestion.full_address || suggestion.place_formatted || 'Point of Interest'}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* No token warning */}
      {(!MAPBOX_TOKEN || MAPBOX_TOKEN === 'your_mapbox_token_here') && (
        <Typography sx={{ 
          color: '#FB923C', 
          fontSize: '0.8rem', 
          marginTop: '8px',
          fontStyle: 'italic'
        }}>
          Please add your Mapbox access token to enable location search
        </Typography>
      )}
    </div>
  );
};

export default MapboxLocationSearch;